"use client";

import {
  type FC,
  type HTMLAttributes,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type ChildrenProp,
  Flex,
  Checkbox,
  Switch,
  Text,
  Spacing,
  FormSectionContext,
  useForm,
} from "@huub-web/ui";

import { containerStyle, headerStyle } from "./OptionalFormRow.css";

export type OptionalFormRowProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange"
> &
  ChildrenProp & {
    id: string;
    label: string;
    description?: string;
    checked?: boolean;
    variant?: "primary" | "secondary";
    disabledContent?: ReactNode;
    disabled?: boolean;
    onChange?: (checked: boolean) => void;
    /**
     * Field key used for agent awareness — maps this toggle to a backend field name.
     * Without this, boolean toggle fields (hasPartner, hasLoans, etc.) are invisible to the agent.
     */
    "data-field-key"?: string;
  };

export const OptionalFormRow: FC<OptionalFormRowProps> = ({
  id,
  checked = false,
  label,
  description,
  variant = "primary",
  disabledContent,
  disabled = false,
  children,
  onChange,
  "data-field-key": dataFieldKey,
  ...props
}) => {
  const [isChecked, setIsChecked] = useState(checked);
  const form = useForm();

  const getValueRef = useRef(() => isChecked);
  getValueRef.current = () => isChecked;

  const sectionKey = useContext(FormSectionContext);

  const handleToggle = () => {
    const newState = !isChecked;
    setIsChecked(newState);
    // Eagerly update the value ref so that notifyValueChanged (which
    // triggers fireSchemaChange → getFieldSchema → getValue) reads the
    // new value, not the stale pre-setState value.
    getValueRef.current = () => newState;
    onChange?.(newState);
    form?.notifyValueChanged();
  };

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  /**
   * ⚠️ AGENT AWARENESS: Register this toggle field with the Form system
   * so the AI agent can see it in the field schema.
   * Without this, boolean toggle fields (hasPartner, hasLoans, etc.)
   * are invisible to the agent.
   */
  useEffect(() => {
    if (!form || !id) return;

    form.registerValidatable(
      id,
      () => ({
        success: true,
        errors: [],
        value: getValueRef.current(),
        name: label,
      }),
      label,
      () => getValueRef.current(),
      {
        fieldKey: dataFieldKey,
        label,
        type: "boolean",
        description,
        ...(sectionKey ? { step: sectionKey } : {}),
      },
    );

    return () => {
      form.deregisterValidatable(id);
    };
    // `form` excluded from deps — its context value gets a new identity on every
    // Form re-render (inline object in Provider), causing an infinite
    // registerValidatable → fireSchemaChange → re-render loop.
  }, [id, label, dataFieldKey, description, sectionKey]);

  return (
    <Flex
      id={id}
      direction="column"
      align="flex-start"
      className={containerStyle({ variant })}
      gap="xs"
      {...props}
    >
      <Flex
        align={description ? "flex-start" : "center"}
        gap="s"
        className={headerStyle()}
      >
        <Flex gap="xxs" direction="column" grow>
          {label && (
            <Text as="label" htmlFor={`${id}__optional`} bold>
              {label}
            </Text>
          )}
          {description && <Text as="p">{description}</Text>}
        </Flex>

        {variant === "primary" ? (
          <Switch
            id={`${id}__optional`}
            checked={isChecked}
            onChange={handleToggle}
            label={label}
            variant="secondary"
            disabled={disabled || form?.disabled}
          />
        ) : (
          <Checkbox
            id={`${id}__optional`}
            checked={isChecked}
            disabled={disabled || form?.disabled}
            onChange={handleToggle}
          />
        )}
      </Flex>

      {isChecked ? (
        <>
          <Spacing />
          {children}
        </>
      ) : disabledContent ? (
        <>
          <Spacing />
          {disabledContent}
        </>
      ) : null}
    </Flex>
  );
};
